import { useAgent } from 'agents/react';
import { useAgentChat } from '@cloudflare/ai-chat/react';
import type { UIMessage } from 'ai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { adminAgentService, type AdminAgentSession } from '../../../../services/adminAgent.service';
import { mergeChatHistory } from '../utils/mergeChatHistory';
import { getBrowserTimeZone } from '../../../../lib/timezone';
import { useAdminSocketTicket } from './useAdminSocketTicket';

/** Class name of the admin chat Durable Object; `agents` kebab-cases it to `admin-agent-d-o`. */
const ADMIN_AGENT_NAME = 'AdminAgentDO';
const CHAT_MODE = 'normal';

export interface AdminChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

function toText(message: UIMessage): string {
  return message.parts
    .map((part) => (part.type === 'text' ? part.text : ''))
    .filter(Boolean)
    .join('\n');
}

function normalizeHost(serverUrl: string): string | undefined {
  if (!serverUrl) return undefined;
  try {
    return new URL(serverUrl).host;
  } catch {
    return serverUrl.replace(/^(https?|wss?):\/\//, '').replace(/\/$/, '');
  }
}

/** Chat with the admin assistant over AdminAgentDO. Connects only while `session` is set. */
export function useAdminAgentChat(session: AdminAgentSession | null) {
  const [sendError, setSendError] = useState<Error | undefined>(undefined);
  // Sent with every message so the agent knows which admin page the admin is looking at.
  const { pathname } = useLocation();
  const { ticket, handleOpen, handleClose } = useAdminSocketTicket(session?.conversationPublicId);
  const host = normalizeHost(import.meta.env.VITE_SERVER_URL || '');

  const agent = useAgent({
    agent: ADMIN_AGENT_NAME,
    name: session?.conversationPublicId ?? '',
    enabled: Boolean(session) && Boolean(ticket),
    onOpen: handleOpen,
    onClose: handleClose,
    ...(ticket ? { query: { ticket } } : {}),
    ...(host ? { host } : {}),
  });

  const {
    messages: rawMessages,
    sendMessage: agentSendMessage,
    status,
    error,
    stop,
  } = useAgentChat<unknown, UIMessage>({
    agent,
    getInitialMessages: null,
    resume: false,
    experimental_throttle: 50,
  });

  const isStreaming = status === 'submitted' || status === 'streaming';

  // Persisted history: the socket state starts empty after every (re)connect, so it is merged in.
  const queryClient = useQueryClient();
  const conversationId = session?.conversationPublicId;
  const historyKey = ['admin-agent', 'messages', conversationId] as const;
  const { data: history } = useQuery({
    queryKey: historyKey,
    queryFn: () => adminAgentService.getConversationMessages(conversationId as string),
    enabled: Boolean(conversationId),
    staleTime: Infinity,
  });
  const wasStreaming = useRef(false);
  useEffect(() => {
    // A finished reply is now persisted: refresh so a later reconnect shows it.
    if (wasStreaming.current && !isStreaming && conversationId) {
      void queryClient.invalidateQueries({ queryKey: historyKey });
    }
    wasStreaming.current = isStreaming;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, conversationId, queryClient]);

  const liveMessages = useMemo<AdminChatMessage[]>(
    () =>
      rawMessages
        .filter((message) => message.role === 'user' || message.role === 'assistant')
        .map((message) => ({
          id: message.id,
          role: message.role as 'user' | 'assistant',
          text: toText(message),
        }))
        .filter((message) => message.text),
    [rawMessages]
  );
  const messages = useMemo(
    () => mergeChatHistory(history ?? [], liveMessages),
    [history, liveMessages]
  );

  const sendMessage = useCallback(
    async (content: string) => {
      const text = content.trim();
      if (!session || !text) return;
      setSendError(undefined);
      try {
        await agentSendMessage({
          text,
          metadata: {
            agentPublicId: session.agentPublicId,
            sessionId: session.sessionId,
            conversationId: session.conversationPublicId,
            mode: CHAT_MODE,
            timezone: getBrowserTimeZone(),
            pageContext: { path: pathname },
          },
        });
      } catch (caught) {
        setSendError(caught instanceof Error ? caught : new Error(String(caught)));
      }
    },
    [agentSendMessage, session, pathname]
  );

  return {
    messages,
    sendMessage,
    stop,
    isStreaming,
    error: sendError ?? error,
  };
}
